import { useState, useRef, useEffect } from 'react';

export default function MarqueeText({
  text = '',
  className = '',
  mode = 'marquee', // 'marquee' | 'wrap'
  speed = 10, // seconds per full loop (twice as fast)
}) {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    if (mode === 'wrap') return;

    const checkOverflow = () => {
      if (containerRef.current && textRef.current) {
        setIsOverflowing(textRef.current.scrollWidth > containerRef.current.clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [text, mode]);

  // Mode: Wrap (clamps cleanly up to 2 lines)
  if (mode === 'wrap') {
    return (
      <p className={`line-clamp-2 leading-snug break-words ${className}`} title={text}>
        {text}
      </p>
    );
  }

  // Mode: Marquee but text fits without overflowing -> static display
  if (!isOverflowing) {
    return (
      <div ref={containerRef} className="w-full min-w-0 overflow-hidden" title={text}>
        <span ref={textRef} className={`block truncate ${className}`}>
          {text}
        </span>
      </div>
    );
  }

  // Mode: Marquee and overflowing -> 100% seamless twin-block moving sign ticker
  return (
    <div
      ref={containerRef}
      className="w-full min-w-0 overflow-hidden relative group cursor-default"
      style={{
        maskImage: 'linear-gradient(to right, black 86%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to right, black 86%, transparent 100%)',
      }}
      title={text}
    >
      <div
        className="animate-marquee-text group-hover:[animation-play-state:paused]"
        style={{ animationDuration: `${speed}s` }}
      >
        {/* Block 1 */}
        <span className="inline-flex items-center shrink-0">
          <span ref={textRef} className={className}>
            {text}
          </span>
          <span className="mx-6 text-gray-400 font-normal opacity-50 select-none">
            •
          </span>
        </span>

        {/* Block 2 (Exact pixel-identical twin of Block 1 for 100% stutter-free infinite loop) */}
        <span className="inline-flex items-center shrink-0" aria-hidden="true">
          <span className={className}>
            {text}
          </span>
          <span className="mx-6 text-gray-400 font-normal opacity-50 select-none">
            •
          </span>
        </span>
      </div>
    </div>
  );
}
